// ErrorState — "request failed" (§7 States, README "Error").
//
// Translated from prototype/screens-states.jsx `Empty` (danger tone variant).
// README: "state what failed, keep the filters, offer Retry + Copy error."
// Keeping the filters is the CALLER's job (it must not reset FilterState on
// a failed fetch) — this component only renders the message and the two
// actions. `errorDetail` is the raw text put on the clipboard by Copy error;
// it is never rendered directly if it isn't safe to show, so callers should
// still pass a friendly `body`.
import React from 'react';
import { Box, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HdButton from '../HdButton';

interface ErrorStateProps {
  /** e.g. "Couldn't load tickets". States what failed. */
  title: string;
  /** e.g. "The request timed out. Your filters are still applied — retrying will use them." */
  body: string;
  /** Raw error text copied to the clipboard by "Copy error". Falls back to `body` if omitted. */
  errorDetail?: string;
  onRetry: () => void;
  /**
   * Called after the copy attempt resolves, with whether it succeeded — lets
   * the caller show its own toast/snackbar. Optional; this component does not
   * render its own confirmation UI.
   */
  onCopied?: (ok: boolean) => void;
}

export default function ErrorState({ title, body, errorDetail, onRetry, onCopied }: ErrorStateProps) {
  const handleCopy = () => {
    const text = errorDetail ?? body;
    if (!navigator.clipboard) {
      onCopied?.(false);
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => onCopied?.(true))
      .catch(() => onCopied?.(false));
  };

  return (
    <Box
      sx={{
        padding: '40px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        textAlign: 'center'
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 'var(--r-md)',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'var(--status-danger-bg)',
          color: 'var(--status-danger-fg)',
          border: '1px solid var(--hairline)'
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 21 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', marginTop: '5px', lineHeight: 1.6, maxWidth: 340 }}>
          {body}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
        <HdButton hdVariant="primary" startIcon={<ReplayIcon sx={{ fontSize: 14 }} />} onClick={onRetry}>
          Retry
        </HdButton>
        <HdButton startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />} onClick={handleCopy}>
          Copy error
        </HdButton>
      </Box>
    </Box>
  );
}
